import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { useSalthubClientsData } from "@/data/use-salthub-clients-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMemo } from "react";

type SalthubClientSelectProps = {
  salthubClientId: string | null;
  disabled?: boolean;
  onChange: (salthubClientId: string | null) => void;
};

/**
 * Shows SaltHub client names. Saves the SaltHub client ID, which is what
 * Salt People returns for a person's clients.
 */
export const SalthubClientSelect: React.FC<SalthubClientSelectProps> = ({
  salthubClientId,
  disabled = false,
  onChange,
}) => {
  const { clients, isLoading, error } = useSalthubClientsData();

  const groups = useMemo(
    () => [
      {
        options: clients.map((client) => ({
          value: client.clientId,
          searchableText: client.clientName,
          render: () => client.clientName,
        })),
      },
    ],
    [clients],
  );

  const selectedName = clients.find(
    (client) => client.clientId === salthubClientId,
  )?.clientName;

  const label = (() => {
    if (!salthubClientId) return isLoading ? "Loading clients..." : "None";
    if (selectedName) return selectedName;
    // Keep an ID saved before the picker, or one SaltHub no longer lists, visible.
    return isLoading
      ? "Loading clients..."
      : `Unknown client (${salthubClientId})`;
  })();

  return (
    <div className="flex flex-row items-center gap-2">
      <Combobox
        groups={groups}
        selected={salthubClientId ? [salthubClientId] : []}
        onSelectionChange={(value) => onChange(value)}
        searchPlaceholder="Search clients..."
        align="start"
        disabled={disabled || !!error || clients.length === 0}
      >
        <Button variant="outline" className="w-64 justify-start truncate">
          {label}
        </Button>
      </Combobox>
      {salthubClientId && (
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={() => onChange(null)}
          aria-label="Clear client"
        >
          <FontAwesomeIcon icon={faXmark} />
        </Button>
      )}
      {error && (
        <span className="text-destructive text-sm">
          Could not load SaltHub clients.
        </span>
      )}
    </div>
  );
};
